import { Partytown } from '@qwik.dev/partytown/react'
import { type ReactNode } from 'react'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { MarketingPageShell } from './components/marketing-page-shell.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { SiteLayout } from './components/site-layout.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { organizationSchema } from './utils/constants.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { mediaAsset } from './utils/media.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { makeTimings } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

const appleTouchIconAssetUrl = mediaAsset(
	'/app-assets/favicons/apple-touch-icon.png',
)
const faviconAssetUrl = mediaAsset('/app-assets/favicons/favicon.svg')
const faviconIcoAssetUrl = mediaAsset('/favicon.ico')

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{ rel: 'icon', href: faviconIcoAssetUrl, sizes: '48x48' },
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const,
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{ title: data ? 'Dealer Pro Edge' : 'Error | Dealer Pro Edge' },
		{
			name: 'description',
			content:
				'Professional automotive dealer websites and digital marketing solutions',
		},
		{ 'script:ld+json': organizationSchema },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
			},
			ENV: getEnv(),
			toast,
			honeyProps,
			// TODO: Move GTM_ID to an environment variable
			GTM_ID: 'G-YQMGGHF5ML',
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

// =============================================================================
// DOCUMENT COMPONENT
// =============================================================================

function Document({
	children,
	nonce,
	env = {},
}: {
	children: ReactNode
	nonce: string
	env?: Record<string, string | undefined>
}) {
	// FIX: Access ENV from env prop instead of global to avoid reference errors
	const allowIndexing = env.ALLOW_INDEXING !== 'false'
	const isClient = typeof document !== 'undefined'
	const loaderData = useLoaderData<typeof loader>()
	const GTM_ID = loaderData?.GTM_ID

	return (
		<html
			lang="en"
			className={`h-full overflow-x-hidden ${isClient ? 'has-js' : ''}`}
		>
			<head>
				{/* Add has-js class immediately to prevent FOUC */}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `document.documentElement.classList.add('has-js')`,
					}}
				/>
				{/* Partytown for offloading analytics to web worker */}
				<Partytown nonce={nonce} debug={false} forward={['dataLayer.push']} />
				{GTM_ID && (
					<>
						<script
							type="text/partytown"
							nonce={nonce}
							src={`https://www.googletagmanager.com/gtag/js?id=${GTM_ID}`}
						/>
						<script
							type="text/partytown"
							nonce={nonce}
							dangerouslySetInnerHTML={{
								__html: `
									window.dataLayer = window.dataLayer || [];
									function gtag(){dataLayer.push(arguments);}
									gtag('js', new Date());
									gtag('config', '${GTM_ID}');
								`,
							}}
						/>
					</>
				)}
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{!allowIndexing && <meta name="robots" content="noindex, nofollow" />}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

// =============================================================================
// LAYOUT COMPONENT
// =============================================================================

export function Layout({ children }: { children: ReactNode }) {
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()

	return (
		<Document nonce={nonce} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	useToast(data.toast)

	return (
		<>
			<SiteLayout>
				<div id="main-content">
					<Outlet />
				</div>
			</SiteLayout>
			<EpicProgress />
		</>
	)
}

// =============================================================================
// APP WITH PROVIDERS
// =============================================================================

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()

	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// Error boundary for unhandled errors
export function ErrorBoundary() {
	return (
		<MarketingPageShell>
			<GeneralErrorBoundary />
		</MarketingPageShell>
	)
}
